import {Box, Typography, Tabs, Tab, Button} from '@mui/material';
import {BackspaceTwoTone, GroupTwoTone, ClearAll} from '@mui/icons-material';
import React, {useMemo, useState, useEffect} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {useAuth} from 'contexts/AuthContext';
import MaterialReactTablePagedKeyset from 'components/MaterialReactTablePagedKeyset';
import {useConfirm} from 'material-ui-confirm';
import {enqueueSnackbar} from 'notistack';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, Stack } from '@mui/material';
import TooltipBigNumber from 'components/TooltipBigNumber';

const StatsWait = ({ stats, flowId, stepId }) => {
    const { axiosElementalApiCurrentOrg } = useAuth();
    const [tabValue, setTabValue] = useState(0);
    const queryClient = useQueryClient();
    const confirm = useConfirm();

    // Local state for domains dialog
    const [flushDialogOpen, setFlushDialogOpen] = useState(false);
    const [domainInput, setDomainInput] = useState('');
    const [domains, setDomains] = useState([]);

    const addDomain = () => {
        const trimmed = (domainInput || '').trim().toLowerCase();
        if (!trimmed) return;
        if (!domains.includes(trimmed)) {
            setDomains(prev => [...prev, trimmed]);
        }
        setDomainInput('');
    };

    const removeDomain = (toRemove) => {
        setDomains(prev => prev.filter(d => d !== toRemove));
    };

    const handleOpenFlushDialog = () => {
        setFlushDialogOpen(true);
    };

    const handleCloseFlushDialog = () => {
        setFlushDialogOpen(false);
        setDomainInput('');
        setDomains([]);
    };

    // Flush mutation now accepts payload with domains
    const flushMutation = useMutation({
        mutationFn: async () => {
            return await axiosElementalApiCurrentOrg.post(`/flows/${flowId}/steps/${stepId}/states/current/flush`, { domains });
        },
        onSuccess: (response) => {
            const flushedCount = response?.data?.flushed?.length || 0;
            enqueueSnackbar(`Successfully flushed ${flushedCount} profiles from the journey`, { variant: 'success' });
            queryClient.invalidateQueries(['flow_step_current_states', flowId, stepId]);
            setFlushDialogOpen(false);
            setDomains([]);
            setDomainInput('');
        },
        onError: (error) => {
            let errorMessage = 'Failed to flush profiles';
            if (typeof error === 'object' && error !== null) {
                if ('response' in error && error.response && typeof error.response === 'object' && 'data' in error.response && error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
                    errorMessage = String(error.response.data.message);
                } else if ('message' in error) {
                    errorMessage = String(error.message);
                }
            }
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }
    });

    // Handle confirm and flush with payload
    const handleConfirmAndFlush = () => {
        confirm({
            title: 'Flush Profiles',
            description: `Do you want to flush ${stats.waiting} profiles from the journey?`,
            confirmationText: 'Flush',
            cancellationText: 'Cancel',
            dialogProps: {
                sx: {
                    zIndex: 1450,
                    '& .MuiDialog-paper': {
                        zIndex: (theme) => 1450 + 1,
                    }
                },
            }
        }).then(() => {
            flushMutation.mutate();
        }).catch(() => {
            // Cancelled
        });
    };

    // Query for current states
    const { data: currentStatesData, isLoading: currentStatesLoading } = useQuery({
        queryKey: ['flow_step_current_states', flowId, stepId],
        queryFn: async () => {
            return await axiosElementalApiCurrentOrg.get(`/flows/${flowId}/steps/${stepId}/states/current`);
        },
        enabled: !!flowId && !!stepId
    });

    // Query for complete states
    const { data: completeStatesData, isLoading: completeStatesLoading } = useQuery({
        queryKey: ['flow_step_complete_states', flowId, stepId],
        queryFn: async () => {
            return await axiosElementalApiCurrentOrg.get(`/flows/${flowId}/steps/${stepId}/states/complete`);
        },
        enabled: !!flowId && !!stepId
    });

    // Columns for the tables
    const columns = useMemo(() => [
        {
            header: 'Email',
            accessorKey: 'profile.email',
            Cell: ({ cell }) => {
                const profile = cell.row.original.profile;
                return profile?.email || 'No email';
            }
        },
        {
            header: 'Created At',
            accessorKey: 'created_at',
            Cell: ({ cell }) => {
                const date = cell.getValue();
                return date ? new Date(date).toLocaleString() : 'N/A';
            }
        }
    ], []);

    // Columns for current states
    const currentStateColumns = useMemo(() => [
        {
            header: 'Email',
            accessorKey: 'profile.email',
            Cell: ({ cell }) => {
                const profile = cell.row.original.profile;
                return profile?.email || 'No email';
            }
        },
        {
            header: 'Entered At',
            accessorKey: 'entered_effective_at',
            Cell: ({ cell }) => {
                const date = cell.getValue();
                return date ? new Date(date).toLocaleString() : 'N/A';
            }
        }
    ], []);

    // Columns for complete states
    const completeStateColumns = useMemo(() => [
        {
            header: 'Email',
            accessorKey: 'profile.email',
            Cell: ({ cell }) => {
                const profile = cell.row.original.profile;
                return profile?.email || 'No email';
            }
        },
        {
            header: 'Exited At',
            accessorKey: 'exited_effective_at',
            Cell: ({ cell }) => {
                const date = cell.getValue();
                return date ? new Date(date).toLocaleString() : 'N/A';
            }
        },
        {
            header: 'Next Action',
            accessorKey: 'process_next_action'
        }
    ], []);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    return (
        <Box className='flow-sidebar__body-hihlighter' display='flex' style={{padding: '5px 0px'}}>
            <Box sx={{width: '100%'}} display='flex' flexDirection='column'>

                <Box display='flex' flexGrow={1} alignItems='center' mb={2}>
                    <GroupTwoTone style={{marginRight: 10}}/>
                    <Typography component='div' variant='subtitle1'>Waiting: <TooltipBigNumber number={stats.waiting}><span style={{fontWeight: 400}}>{stats.waiting}</span></TooltipBigNumber></Typography>
                    {stats.waiting > 0 && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ClearAll />}
                            onClick={handleOpenFlushDialog}
                            disabled={flushMutation.isLoading}
                            sx={{ ml: 2 }}
                        >
                            Flush
                        </Button>
                    )}
                </Box>
                <Box display='flex' flexGrow={1} alignItems='center' mb={2}>
                    <GroupTwoTone style={{marginRight: 10}}/>
                    <Typography component='div' variant='subtitle1'>Total Travellers: <span style={{fontWeight: 400}}>{stats.journeyed}</span></Typography>
                </Box>

                <Box display='flex' flexGrow={1} alignItems='center' mb={2}>
                    <BackspaceTwoTone style={{marginRight: 10}}/>
                    <Typography component='div' variant='subtitle1'>Exited:</Typography>
                </Box>
{Object.entries(stats.completed_by_exit_result || {}).map(([key, value]) => (
                <Box key={key} display='flex' flexGrow={1} alignItems='center' mb={2}>
                    <Typography component='div' variant='subtitle1'>
                        {key}: <span style={{fontWeight: 400}}>{value}</span>
                    </Typography>
                </Box>
))}
                {/* <Box display='flex' flexGrow={1} alignItems='center' mb={2}>
                    <Typography component='div' variant='subtitle1'> <span style={{fontWeight: 400}}>{stats.bypass_filter}</span></Typography>
                </Box> */}

                {/* New tabs for current and complete states */}
                {flowId && stepId && (
                    <Box mt={3}>
                        <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
                            <Tab label="Current States" />
                            <Tab label="Complete States" />
                        </Tabs>
                        
                        <Box mt={2}>
                            {tabValue === 0 && (
                                <MaterialReactTablePagedKeyset
                                    queryBaseKey={['flow_step_current_states', flowId, stepId]}
                                    collectionName={'Current States'}
                                    columns={currentStateColumns}
                                    queryBaseUrl={`/flows/${flowId}/steps/${stepId}/states/current`}
                                    axiosInstance={axiosElementalApiCurrentOrg}
                                    queryOptions={{
                                        enabled: !!flowId && !!stepId
                                    }}
                                />
                            )}
                            
                            {tabValue === 1 && (
                                <MaterialReactTablePagedKeyset
                                    queryBaseKey={['flow_step_complete_states', flowId, stepId]}
                                    collectionName={'Complete States'}
                                    columns={completeStateColumns}
                                    queryBaseUrl={`/flows/${flowId}/steps/${stepId}/states/complete`}
                                    axiosInstance={axiosElementalApiCurrentOrg}
                                    queryOptions={{
                                        enabled: !!flowId && !!stepId
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                )}

                {/* Domains selection dialog */}
                <Dialog 
                    open={flushDialogOpen}
                    onClose={handleCloseFlushDialog}
                    sx={{
                        zIndex: 1450,
                        '& .MuiDialog-paper': { zIndex: (theme) => 1450 + 2 }
                    }}
                >
                    <DialogTitle>Flush Current States</DialogTitle>
                    <DialogContent>
                        <Typography variant='body2' sx={{ mb: 1 }}>
                            Optionally specify one or more domains to filter which profiles will be flushed.
                        </Typography>
                        <Stack direction='row' spacing={1} alignItems='center'>
                            <TextField
                                size='small'
                                label='Domain (example.com)'
                                value={domainInput}
                                onChange={(e) => setDomainInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addDomain();
                                    }
                                }}
                            />
                            <Button variant='contained' onClick={addDomain}>Add</Button>
                        </Stack>
                        <Stack direction='row' spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                            {domains.map((d) => (
                                <Chip key={d} label={d} onDelete={() => removeDomain(d)} />
                            ))}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseFlushDialog}>Cancel</Button>
                        <Button onClick={handleConfirmAndFlush} variant='contained' disabled={flushMutation.isLoading}>Flush</Button>
                    </DialogActions>
                </Dialog>

                {/*<Box display='flex' flexGrow={1} alignItems='center' mb={2}>*/}
                {/*    <LocalAtmTwoTone style={{marginRight: 10}}/>*/}
                {/*    <Typography component='div' variant='subtitle1'>Converted while Waiting: {totalConversions}</Typography>*/}
                {/*</Box>*/}
            </Box>
        </Box>
    );
};

export default StatsWait;